import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SEOHead from './SEOHead';

const {
  FiArrowLeft, FiArrowRight, FiCheck, FiX, FiEdit3, FiGrid, FiSliders, FiZap,
  FiCheckCircle, FiUsers, FiDownload, FiAlertTriangle, FiMessageSquare, FiBookOpen, FiTarget, FiShare2,
  FiShoppingBag, FiHeart, FiUploadCloud
} = FiIcons;

const integrations = [
  { title: 'Publish to stores', icon: FiUploadCloud,
    items: ['Amazon KDP', 'IngramSpark', 'Apple Books', 'Kobo', 'Google Play Books', 'Draft2Digital'] },
  { title: 'Sell direct', icon: FiShoppingBag,
    items: ['Gumroad', 'Payhip', 'Shopify', 'Lulu', 'Your own site (reader embed)'] },
  { title: 'Grow your audience', icon: FiHeart,
    items: ['Patreon', 'Substack', 'Ko-fi', 'Mailchimp', 'ConvertKit'] }
];

/* ---------- realistic in-browser app mockups ---------- */

const BrowserFrame = ({ title, children }) => (
  <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-xl shadow-2xl overflow-hidden">
    <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-700">
      <span className="w-3 h-3 rounded-full bg-red-500" />
      <span className="w-3 h-3 rounded-full bg-yellow-500" />
      <span className="w-3 h-3 rounded-full bg-green-500" />
      <span className="mx-auto text-xs text-gray-400">{title}</span>
    </div>
    <div className="bg-gray-50">{children}</div>
  </div>
);

const Bookshelf = () => {
  const books = [
    { t: 'The Lighthouse Year', g: 'Contemporary Romance', pct: 68, words: '54,200', color: 'from-rose-400 to-secondary-500' },
    { t: 'The Salt Path Home', g: 'Literary Fiction', pct: 100, words: '82,900', color: 'from-primary-400 to-primary-600' },
    { t: 'Quiet Harbor', g: 'Cozy Mystery', pct: 35, words: '28,100', color: 'from-secondary-400 to-amber-600' },
    { t: 'Signal & Noise', g: 'Thriller', pct: 12, words: '9,400', color: 'from-slate-500 to-slate-700' }
  ];
  return (
    <BrowserFrame title="ManuscriptSmith — My Bookshelf">
      <div className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="font-semibold text-gray-800">My Bookshelf</div>
          <div className="text-xs px-3 py-1.5 rounded-lg bg-primary-600 text-white font-medium">+ New book</div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {books.map((b) => (
            <div key={b.t} className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
              <div className={`h-24 bg-gradient-to-br ${b.color} flex items-end p-2`}>
                <span className="text-[10px] text-white/90 font-semibold leading-tight">{b.t}</span>
              </div>
              <div className="p-2.5">
                <div className="text-[11px] font-semibold text-gray-800 truncate">{b.t}</div>
                <div className="text-[10px] text-gray-400 mb-2">{b.g}</div>
                <div className="w-full bg-gray-100 rounded-full h-1.5">
                  <div className="h-1.5 rounded-full bg-primary-500" style={{ width: `${b.pct}%` }} />
                </div>
                <div className="flex justify-between text-[10px] text-gray-400 mt-1">
                  <span>{b.pct}%</span><span>{b.words} w</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </BrowserFrame>
  );
};

const Editor = () => (
  <BrowserFrame title="ManuscriptSmith — Editor · The Lighthouse Year">
    <div className="flex h-[330px] text-left">
      <div className="hidden sm:block w-44 bg-white border-r border-gray-200 p-3 overflow-hidden">
        <div className="text-[10px] font-bold uppercase text-gray-400 mb-2">Chapters</div>
        {['1 · The Pier', '2 · Rain in June', '3 · Theo', '4 · What She Knew', '5 · The Letter'].map((c, i) => (
          <div key={c} className={`text-[11px] px-2 py-1.5 rounded mb-1 ${i === 2 ? 'bg-primary-50 text-primary-700 font-semibold' : 'text-gray-600'}`}>{c}</div>
        ))}
        <div className="mt-3 text-[10px] text-gray-400">Today · 1,240 words</div>
        <div className="w-full bg-gray-100 rounded-full h-1.5 mt-1"><div className="h-1.5 rounded-full bg-secondary-500" style={{ width: '62%' }} /></div>
      </div>
      <div className="flex-1 bg-white p-6 overflow-hidden">
        <div className="max-w-md mx-auto">
          <div className="text-xs text-gray-400 mb-3">Chapter 3 · Theo</div>
          <p className="text-sm text-gray-800 leading-7 font-serif" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>
            Theo arrived the way weather does — without asking, and impossible to ignore. She had spent the
            morning rehearsing what she would say, and now, watching him shake the rain from his coat in her
            doorway, every careful sentence <span className="bg-secondary-100">left her at once.</span>
          </p>
          <div className="mt-3 inline-flex items-center gap-2 text-[11px] text-primary-700 bg-primary-50 px-2 py-1 rounded">
            <SafeIcon icon={FiEdit3} className="w-3 h-3" /> Continue in your voice
          </div>
        </div>
      </div>
    </div>
  </BrowserFrame>
);

const VoiceCrafting = () => {
  const dims = [['Sentence rhythm', 74], ['Vocabulary', 61], ['Tone — warm', 80], ['Imagery', 86], ['Dialogue', 68], ['Pace', 44]];
  return (
    <BrowserFrame title="ManuscriptSmith — Voice DNA">
      <div className="p-5 grid sm:grid-cols-2 gap-5 text-left">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-xs font-bold uppercase text-gray-400 mb-3">Your style profile</div>
          {dims.map(([l, v]) => (
            <div key={l} className="mb-2.5">
              <div className="flex justify-between text-[11px] text-gray-500 mb-1"><span>{l}</span><span>{v}</span></div>
              <div className="w-full bg-gray-100 rounded-full h-2"><div className="h-2 rounded-full bg-gradient-to-r from-primary-500 to-secondary-500" style={{ width: `${v}%` }} /></div>
            </div>
          ))}
          <div className="mt-3 text-[11px] text-primary-700 bg-primary-50 rounded px-2 py-1.5 inline-flex items-center gap-1">
            <SafeIcon icon={FiCheckCircle} className="w-3 h-3" /> Trained on 3 of your chapters
          </div>
        </div>
        <div className="space-y-3">
          <div className="bg-white rounded-lg border border-gray-200 p-3">
            <div className="text-[10px] font-bold uppercase text-gray-400 mb-1">Generic AI</div>
            <p className="text-[12px] text-gray-500 italic">"He entered the room. She was surprised to see him."</p>
          </div>
          <div className="bg-white rounded-lg border-2 border-primary-200 p-3">
            <div className="text-[10px] font-bold uppercase text-primary-500 mb-1">In your voice</div>
            <p className="text-[12px] text-gray-800 italic">"He came in the way he always did — quietly, and then all at once. She had not braced for the all-at-once."</p>
          </div>
        </div>
      </div>
    </BrowserFrame>
  );
};

const AiTools = () => (
  <BrowserFrame title="ManuscriptSmith — AI Writing Tools">
    <div className="p-6 bg-white text-left h-[300px]">
      <div className="max-w-md mx-auto relative">
        <p className="text-sm text-gray-800 leading-7">
          She read the letter twice. <span className="bg-primary-100 px-0.5">The handwriting was Theo's, but the words were not the ones she expected.</span>
        </p>
        {/* floating AI menu */}
        <div className="mt-4 w-64 bg-gray-900 text-white rounded-lg shadow-2xl overflow-hidden">
          <div className="px-3 py-2 text-[10px] uppercase tracking-wide text-gray-400 border-b border-gray-700">AI · in your voice</div>
          {[['Continue', FiArrowRight], ['Rewrite', FiEdit3], ['Describe more', FiZap], ['Tighten', FiTarget], ['Outline from here', FiGrid]].map(([l, ic]) => (
            <div key={l} className="px-3 py-2 text-[12px] flex items-center gap-2 hover:bg-gray-800">
              <SafeIcon icon={ic} className="w-3.5 h-3.5 text-secondary-400" /> {l}
            </div>
          ))}
        </div>
      </div>
    </div>
  </BrowserFrame>
);

const Preflight = () => {
  const checks = [
    ['Trim size 6 × 9 in', 'ok'], ['Mirror margins / gutter', 'ok'], ['Chapter headings styled', 'ok'],
    ['Table of contents linked', 'ok'], ['Front matter present', 'warn'], ['Metadata & ISBN', 'warn'], ['Embedded fonts', 'ok']
  ];
  return (
    <BrowserFrame title="ManuscriptSmith — KDP Preflight">
      <div className="p-5 text-left">
        <div className="flex items-center justify-between mb-3">
          <div className="font-semibold text-gray-800 text-sm">KDP Preflight</div>
          <div className="text-[11px] px-2 py-1 rounded-full bg-amber-100 text-amber-700 font-semibold">2 to fix</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
          {checks.map(([c, s]) => (
            <div key={c} className="flex items-center justify-between px-3 py-2">
              <div className="flex items-center gap-2 text-[12px] text-gray-700">
                <SafeIcon icon={s === 'ok' ? FiCheckCircle : FiAlertTriangle} className={`w-4 h-4 ${s === 'ok' ? 'text-green-500' : 'text-amber-500'}`} />
                {c}
              </div>
              {s === 'warn' && <span className="text-[10px] px-2 py-0.5 rounded bg-primary-600 text-white">Fix</span>}
            </div>
          ))}
        </div>
      </div>
    </BrowserFrame>
  );
};

const Collab = () => (
  <BrowserFrame title="ManuscriptSmith — Collaboration · The Lighthouse Year">
    <div className="p-6 bg-white text-left h-[300px]">
      <div className="max-w-lg mx-auto">
        <p className="text-sm text-gray-800 leading-7 font-serif" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>
          Theo arrived the way weather does — <span className="bg-secondary-100">without asking, and impossible to ignore.</span>
        </p>
        <div className="mt-4 flex items-start gap-3">
          <img src="https://images.unsplash.com/photo-1551836022-deb4988cc6c0?auto=format&fit=crop&w=80&h=80&q=80" alt="Linda" className="w-9 h-9 rounded-full object-cover" loading="lazy" />
          <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-[12px] text-gray-700">
            <span className="font-semibold text-gray-900">Linda</span> <span className="text-gray-400">· editor</span>
            <p className="mt-0.5">Love this line — maybe cut "impossible to ignore"? The weather image already does it.</p>
            <div className="mt-1 flex gap-3 text-[11px] text-primary-600"><span>Reply</span><span>Resolve</span></div>
          </div>
        </div>
        <div className="mt-3 text-[11px] text-gray-400 inline-flex items-center gap-1">
          <SafeIcon icon={FiUsers} className="w-3.5 h-3.5" /> 2 people · changes tracked
        </div>
      </div>
    </div>
  </BrowserFrame>
);

const ShareReader = () => (
  <BrowserFrame title="ManuscriptSmith — Share & Publish">
    <div className="p-5 grid sm:grid-cols-2 gap-5 text-left">
      {/* embeddable reader preview */}
      <div className="bg-white rounded-lg border border-gray-200 p-3">
        <div className="text-[10px] font-bold uppercase text-gray-400 mb-2">Public reader · your site</div>
        <div className="mx-auto w-40 h-48 bg-gradient-to-b from-parchment-50 to-parchment-100 border border-gray-200 rounded shadow-inner p-3 overflow-hidden">
          <div className="text-[9px] text-secondary-700 font-semibold mb-1">The Lighthouse Year</div>
          <div className="text-[8px] text-gray-500 leading-[1.5] font-serif" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>
            Theo arrived the way weather does — without asking, and impossible to ignore. She had spent the morning rehearsing what she would say…
          </div>
          <div className="mt-2 flex justify-between text-[8px] text-gray-400"><span>‹ prev</span><span>Ch. 3</span><span>next ›</span></div>
        </div>
      </div>
      <div className="space-y-3">
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <div className="text-[10px] font-bold uppercase text-gray-400 mb-1">Share link</div>
          <div className="text-[11px] font-mono bg-gray-50 rounded px-2 py-1 text-gray-600 truncate">read.yoursite.com/love-with-kimball</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <div className="text-[10px] font-bold uppercase text-gray-400 mb-1">Embed on your website</div>
          <div className="text-[10px] font-mono bg-gray-900 text-green-300 rounded px-2 py-1 truncate">&lt;iframe src="…/reader"&gt;</div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex -space-x-2">
            <img src="https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=64&h=64&q=80" className="w-7 h-7 rounded-full ring-2 ring-white object-cover" loading="lazy" alt="" />
            <img src="https://images.unsplash.com/photo-1551836022-deb4988cc6c0?auto=format&fit=crop&w=64&h=64&q=80" className="w-7 h-7 rounded-full ring-2 ring-white object-cover" loading="lazy" alt="" />
          </div>
          <span className="text-[11px] text-gray-500">Co-author + editor invited</span>
        </div>
      </div>
    </div>
  </BrowserFrame>
);

const AuthorAiMock = () => (
  <BrowserFrame title="ManuscriptSmith — Your Author AI">
    <div className="p-5 grid sm:grid-cols-2 gap-5 text-left">
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="text-xs font-bold uppercase text-gray-400 mb-3">What your AI knows</div>
        {[['📄','Life timeline & bio','2 sources'],['🔄','Drive folder','synced · 6 docs'],['📧','Forwarded emails','via your private address'],['📷','Photo timeline','dates & albums only']].map(([i,l,d]) => (
          <div key={l} className="flex items-center gap-2 mb-2.5 text-[12px]">
            <span>{i}</span><span className="font-semibold text-gray-800">{l}</span>
            <span className="ml-auto text-[10px] text-gray-400">{d}</span>
          </div>
        ))}
        <div className="mt-2 text-[11px] text-emerald-700 bg-emerald-50 rounded px-2 py-1.5 inline-flex items-center gap-1">
          <SafeIcon icon={FiCheckCircle} className="w-3 h-3" /> Test what your AI knows — anytime
        </div>
      </div>
      <div className="space-y-3">
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <div className="text-[10px] font-bold uppercase text-gray-400 mb-1">Privacy Vault</div>
          <p className="text-[12px] text-gray-700">Phone number found in "Life timeline"</p>
          <p className="text-[11px] text-gray-500 mt-1">Visible to you · <span className="font-semibold text-emerald-700">[REDACTED] from every AI</span> until you authorize it</p>
        </div>
        <div className="bg-white rounded-lg border-2 border-emerald-200 p-3">
          <div className="text-[10px] font-bold uppercase text-emerald-600 mb-1">In the editor</div>
          <p className="text-[12px] text-gray-800 italic">"…the courtyard from the summer of 2003 — the one from your PRIDE album — could open chapter two."</p>
        </div>
      </div>
    </div>
  </BrowserFrame>
);

const features = [
  { id: 'bookshelf', tag: 'Your Bookshelf', icon: FiBookOpen, mock: Bookshelf,
    h: 'Every book in one place', p: "All your projects on one shelf — covers, genres, word counts, and progress at a glance. Vitaly's The Lighthouse Year sits next to the finished manuscripts, so you always know what's next.",
    edge: 'Scrivener keeps each project in a separate file; generic AI chats have no concept of a book at all.' },
  { id: 'editor', tag: 'The Editor', icon: FiEdit3, mock: Editor,
    h: 'A distraction-free editor built for books', p: 'Chapters and scenes in the sidebar, a clean page in the middle, and your daily word-count goal always in view. Focus mode hides everything but the words.',
    edge: 'A real long-form editor — not a chat window, not a blank doc you have to format yourself.' },
  { id: 'voice', tag: 'Voice Crafting', icon: FiSliders, mock: VoiceCrafting,
    h: 'Author Voice DNA — write like yourself', p: 'Train a private style profile from your own chapters across seven dimensions, then watch generic lines become unmistakably yours. This is the part no other writing tool has.',
    edge: 'Sudowrite and ChatGPT give you one blended "AI voice." ManuscriptSmith learns and writes in YOURS.' },
  { id: 'author-ai', tag: 'Your Author AI', icon: FiTarget, mock: AuthorAiMock,
    h: 'An AI that knows your life — and guards it', p: 'Feed it your bio, timelines, documents, whole archives, Drive share links, forwarded emails — even your photo library becomes a life timeline. Every suggestion is grounded in your real facts, and the Privacy Vault keeps sensitive details visible to you but redacted from every AI until you authorize each one.',
    edge: 'No other writing tool has a per-author knowledge base with span-level privacy control. Audit exactly what your AI knows, anytime.' },
  { id: 'ai', tag: 'AI Writing Tools', icon: FiZap, mock: AiTools,
    h: 'AI that continues, rewrites, and outlines — in your voice', p: 'Select any passage and continue, rewrite, describe, tighten, or outline from here — every suggestion generated to match your Voice DNA, never a generic blend.',
    edge: 'Grounded in your style profile, so the AI sounds like you instead of like every other AI book.' },
  { id: 'preflight', tag: 'KDP Preflight', icon: FiCheckCircle, mock: Preflight,
    h: 'Catch KDP rejections before you upload', p: 'Trim size, margins, headings, table of contents, front matter, and metadata are checked live as you write — with one-click fixes — so your book passes on the first try.',
    edge: 'Scrivener/Word leave formatting to you; Reedsy checks late. ManuscriptSmith checks as you write.' },
  { id: 'collab', tag: 'Co-authoring & Editing', icon: FiUsers, mock: Collab,
    h: 'Co-write and edit together, in the same manuscript', p: "Invite a co-author to write beside you and an editor like Linda to leave comments and tracked changes — same document, real time, with roles and permissions. No more emailing Word files back and forth.",
    edge: 'Real co-authorship + editorial collaboration on the manuscript itself, with everyone on the same KDP-ready draft.' },
  { id: 'share', tag: 'Share & Publish', icon: FiShare2, mock: ShareReader,
    h: 'Publish to your own site with a built-in reader', p: "Share a polished public reader link or embed your book straight into your own author website (the way Vitaly showcases The Lighthouse Year on kimballandvitaly.com) — for beta readers, launch pages, or selling direct. Share a single chapter or the whole book.",
    edge: 'Most writing tools stop at an export file. ManuscriptSmith also gives you a shareable, embeddable reader for your own site.' }
];

const vendors = ['ManuscriptSmith', 'Scrivener', 'Atticus', 'Reedsy', 'Sudowrite', 'Generic AI'];
const rows = [
  ['Writes in YOUR voice (Voice DNA)', [true, false, false, false, 'Generic', 'Generic']],
  ['AI grounded in YOUR life facts (Author AI)', [true, false, false, false, false, false]],
  ['Privacy vault — secrets redacted from AI', [true, false, false, false, false, false]],
  ['AI drafting / rewrite / outline', [true, false, false, false, true, true]],
  ['Distraction-free long-form editor', [true, true, true, true, false, false]],
  ['Live KDP Preflight + fixes', [true, false, 'Format only', 'Late check', false, false]],
  ['One-click EPUB / PDF / DOCX / KDP', [true, 'Manual', true, true, false, false]],
  ['Bookshelf / multi-book catalog', [true, false, true, false, false, false]],
  ['Co-authoring + editor comments', [true, 'Limited', false, true, 'Limited', false]],
  ['Embeddable reader for your own site', [true, false, false, false, false, false]],
  ['Audiobook add-on', [true, false, false, false, false, false]],
  ['Your work stays private', [true, true, true, 'Varies', 'Varies', 'Varies']]
];

const Mark = ({ v }) => {
  if (v === true) return <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mx-auto" />;
  if (v === false) return <SafeIcon icon={FiX} className="w-4 h-4 text-gray-300 mx-auto" />;
  return <span className="text-[11px] text-gray-500">{v}</span>;
};

const HowItWorksPage = () => {
  const structuredData = {
    '@context': 'https://schema.org', '@type': 'WebPage',
    name: 'How ManuscriptSmith Works',
    description: 'A guided tour of the ManuscriptSmith writing studio — bookshelf, editor, Author Voice DNA, AI tools, KDP Preflight, and collaboration — and how it compares to other writing tools.',
    url: 'https://manuscriptsmith.com/how-it-works'
  };
  return (
    <div className="min-h-screen bg-gradient-to-br from-parchment-50 to-parchment-100">
      <SEOHead
        title="How It Works — The Writing Studio, Screen by Screen | ManuscriptSmith"
        description="See ManuscriptSmith in action: the bookshelf, distraction-free editor, Author Voice DNA, AI writing tools, live KDP Preflight, and editor collaboration — and how it compares to Scrivener, Atticus, Reedsy, and Sudowrite."
        keywords="manuscriptsmith features, ai writing studio tour, voice dna, kdp preflight, scrivener vs, sudowrite vs, atticus vs, reedsy vs, book writing software comparison"
        canonicalUrl="https://manuscriptsmith.com/how-it-works"
        structuredData={structuredData}
      />

      <main>
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-10 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-5">
            The whole studio, <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-secondary-500">screen by screen</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            From a blank page to a KDP-ready book - here's everything ManuscriptSmith does that a chat window and a
            word processor can't. Real authors, like Vitaly finishing <em>The Lighthouse Year</em> with his editor
            Linda, use every one of these.
          </p>
        </section>

        {features.map((f, i) => {
          const Mock = f.mock;
          return (
            <section key={f.id} className={i % 2 ? 'bg-white py-14' : 'py-14'}>
              <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-center">
                <motion.div
                  className={i % 2 ? 'lg:order-2' : ''}
                  initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
                >
                  <Mock />
                </motion.div>
                <div className={i % 2 ? 'lg:order-1' : ''}>
                  <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-secondary-700 bg-secondary-50 px-3 py-1 rounded-full mb-4">
                    <SafeIcon icon={f.icon} className="w-4 h-4" /> {f.tag}
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-3">{f.h}</h2>
                  <p className="text-lg text-gray-600 mb-4">{f.p}</p>
                  <div className="flex items-start gap-2 text-sm text-primary-800 bg-primary-50 border border-primary-100 rounded-lg p-3">
                    <SafeIcon icon={FiTarget} className="w-4 h-4 mt-0.5 flex-shrink-0 text-primary-600" />
                    <span><strong>What others don't do:</strong> {f.edge}</span>
                  </div>
                </div>
              </div>
            </section>
          );
        })}

        {/* Integrations */}
        <section className="bg-white py-16 border-t border-parchment-200">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-3">Publish and sell wherever your readers are</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Export clean, store-ready files you can upload to every major store, direct-sales platform,
                or membership site you already use - the studio gets your book ready to ship and earn.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {integrations.map((cat) => (
                <motion.div
                  key={cat.title}
                  className="bg-gradient-to-br from-parchment-50 to-white rounded-2xl border border-gray-200 p-6"
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-primary-500 to-secondary-500 flex items-center justify-center mb-4">
                    <SafeIcon icon={cat.icon} className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-3">{cat.title}</h3>
                  <div className="flex flex-wrap gap-2">
                    {cat.items.map((it) => (
                      <span key={it} className="text-xs font-medium px-3 py-1.5 rounded-full bg-white border border-gray-200 text-gray-700">{it}</span>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Linda use-case */}
            <div className="mt-10 bg-gradient-to-r from-primary-50 to-secondary-50 border border-primary-100 rounded-2xl p-6 flex flex-col sm:flex-row items-center gap-5">
              <div className="w-14 h-14 rounded-full bg-gradient-to-r from-primary-500 to-secondary-500 flex items-center justify-center flex-shrink-0">
                <SafeIcon icon={FiHeart} className="w-7 h-7 text-white" />
              </div>
              <p className="text-gray-700 text-center sm:text-left">
                <strong>Linda writes short.</strong> She drafts focused 10,000-word ebooks in her own voice, runs KDP
                Preflight, exports clean - and sells them straight to her <strong>Patreon</strong> members with a
                shareable reader link. Write, polish, publish, earn: one studio.
              </p>
            </div>
            <p className="text-center text-xs text-gray-400 mt-4">
              Store exports work with all major platforms today; direct-sell and audience integrations roll out over time.
            </p>
          </div>
        </section>

        {/* Full comparison */}
        <section className="bg-white py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-3">How ManuscriptSmith compares</h2>
              <p className="text-lg text-gray-600">Writing, voice, formatting, and publishing - in one place, not five.</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse min-w-[760px]">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-3 px-3 text-gray-700">Capability</th>
                    {vendors.map((v, i) => (
                      <th key={v} className={`py-3 px-3 text-sm ${i === 0 ? 'text-primary-600 font-bold' : 'text-gray-500 font-medium'}`}>{v}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r[0]} className="border-b border-gray-100">
                      <td className="py-3 px-3 text-sm text-gray-800 font-medium">{r[0]}</td>
                      {r[1].map((val, j) => (
                        <td key={j} className={`py-3 px-3 text-center ${j === 0 ? 'bg-primary-50/40' : ''}`}><Mark v={val} /></td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-400 mt-4 text-center">Comparison reflects typical positioning of each tool; features evolve.</p>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl p-10 text-center text-white shadow-xl">
            <h2 className="text-3xl font-bold mb-4">Write your book in your own voice</h2>
            <p className="text-lg opacity-90 mb-8">Bring a draft or begin fresh, and invite your editor when you're ready.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/signup" className="inline-flex items-center justify-center px-8 py-4 bg-white text-primary-600 font-bold text-lg rounded-lg hover:bg-gray-50">
                Get started <SafeIcon icon={FiArrowRight} className="w-5 h-5 ml-2" />
              </Link>
              <Link to="/voice-dna" className="inline-flex items-center justify-center px-8 py-4 bg-white/15 border border-white/40 text-white font-semibold text-lg rounded-lg hover:bg-white/25">
                Explore Voice DNA
              </Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default HowItWorksPage;
